import React, { useState, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import api from "../services/api";
import KoraxAssistant from "../components/KoraxAssistant";
import AICreditsWidget from "../components/AICreditsWidget";
import clsx from "clsx";

import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
} from "@material-ui/core";

import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CachedIcon from "@material-ui/icons/Cached";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import { toast } from "react-toastify";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import ChatPopover from "../pages/Chat/ChatPopover";

import { useDate } from "../hooks/useDate";
import UserLanguageSelector from "../components/UserLanguageSelector";

import ColorModeContext from "./themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import KoraxGuide from "../components/KoraxGuide";

// logos (fallbacks)
import logo from "../assets/logo.png";
import logoDark from "../assets/logo-black.png";

const backendUrl = getBackendUrl();
const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary,
      border:
        theme.mode === "light"
          ? "1px solid rgba(0 124 102)"
          : "1px solid rgba(255, 255, 255, 0.5)",
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary,
    },
  },

  chip: { background: "red", color: "white" },
  avatar: { width: "100%" },

  toolbar: {
    paddingRight: 16,
    paddingLeft: 10,
    color: "#ffffff",
    background: "linear-gradient(135deg, #0B1120 0%, #153EAD 100%)",
    gap: theme.spacing(1),
    overflow: "visible",
    minHeight: 50,
    borderBottom: "1px solid rgba(59,130,246,0.16)",
    boxShadow: "0 10px 30px rgba(2,6,23,0.22)",
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      minHeight: 48,
      gap: theme.spacing(0.5),
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
    },
  },

  // SCROLLER HORIZONTAL (ícones)
  topbarScroller: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flex: "1 1 0%",
    minWidth: 0,
    maxWidth: "100%",
    flexWrap: "nowrap",

    // DESKTOP: alinhar à direita
    justifyContent: "flex-end",
    overflowX: "visible",

    // cada filho não encolhe => gera overflow quando somar mais que a largura
    "& > *": { flex: "0 0 auto" },

    // MOBILE: alinhar à esquerda + scroll horizontal invisível
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      touchAction: "pan-x",
      overscrollBehaviorX: "contain",
      msOverflowStyle: "none",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },
  topbarGreetingCard: {
    display: "flex",
    alignItems: "center",
    minHeight: 40,
    padding: "6px 14px",
    borderRadius: 12,
    background:
      theme.mode === "light"
        ? "rgba(255, 255, 255, 0.16)"
        : "rgba(15, 23, 42, 0.42)",
    border:
      theme.mode === "light"
        ? "1px solid rgba(255, 255, 255, 0.22)"
        : "1px solid rgba(148, 163, 184, 0.2)",
    backdropFilter: "blur(6px)",
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  topbarGreetingText: {
    fontSize: 13,
    fontWeight: 500,
    color: "#f8fafc",
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    textShadow: "0 1px 2px rgba(0,0,0,0.22)",
  },
  topbarActionsCard: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 6px",
    borderRadius: 14,
    background:
      theme.mode === "light"
        ? "rgba(15, 23, 42, 0.14)"
        : "rgba(2, 6, 23, 0.5)",
    border:
      theme.mode === "light"
        ? "1px solid rgba(255, 255, 255, 0.18)"
        : "1px solid rgba(148, 163, 184, 0.18)",
    backdropFilter: "blur(6px)",
  },
  topbarActionButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    minWidth: "auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    border:
      theme.mode === "light"
        ? "1px solid rgba(255, 255, 255, 0.16)"
        : "1px solid rgba(148, 163, 184, 0.16)",
    background:
      theme.mode === "light"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(51, 65, 85, 0.45)",
    color: "#f8fafc",
    padding: 6,
    "&:hover": {
      transform: "translateY(-1px)",
      background:
        theme.mode === "light"
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(51, 65, 85, 0.72)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.08rem",
    },
  },
  topbarActionWrapper: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiIconButton-root": {
      width: 34,
      height: 34,
      borderRadius: 10,
      padding: 6,
      color: "#f8fafc",
      border:
        theme.mode === "light"
          ? "1px solid rgba(255, 255, 255, 0.16)"
          : "1px solid rgba(148, 163, 184, 0.16)",
      background:
        theme.mode === "light"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(51, 65, 85, 0.45)",
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        background:
          theme.mode === "light"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(51, 65, 85, 0.72)",
      },
    },
    "& .MuiButton-root": {
      width: 34,
      height: 34,
      minWidth: "auto",
      borderRadius: 10,
      padding: 6,
      color: "#f8fafc",
      border:
        theme.mode === "light"
          ? "1px solid rgba(255, 255, 255, 0.16)"
          : "1px solid rgba(148, 163, 184, 0.16)",
      background:
        theme.mode === "light"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(51, 65, 85, 0.45)",
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        background:
          theme.mode === "light"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(51, 65, 85, 0.72)",
      },
    },
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundSize: "cover",
    padding: "0 8px",
    minHeight: "64px",
    [theme.breakpoints.down("sm")]: { height: "56px" },
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    // no mobile, não desloca a barra ao abrir o drawer temporário
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
  },

  menuButtonHidden: { display: "none" },

  title: {
    flexGrow: 0,
    fontSize: 13,
    color: "white",
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down("sm")]: { display: "none" },
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden",
    borderRight: theme.mode === "light"
      ? "1px solid rgba(0,102,255,0.1)"
      : "1px solid rgba(255,255,255,0.08)",
    backgroundColor: "#0C152B",
  },
  drawerPaperClose: {
    backgroundColor: "#0C152B",
    overflowX: "hidden",
    overflowY: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: { width: theme.spacing(9) },
  },

  appBarSpacer: { minHeight: 48, flexShrink: 0, background: "#0F172A", fontSize: 0, color: "transparent", userSelect: "none" },

  content: { flex: 1, overflow: "hidden", position: "relative", display: "flex", flexDirection: "column", minHeight: 0 },

  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "scroll",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: "8px",
    border: "2px solid transparent",
    "&::-webkit-scrollbar": { display: "none" },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
  },

  pageViewport: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,

    overflowY: "auto",
    overflowX: "hidden",

    display: "flex",
    flexDirection: "column",

    WebkitOverflowScrolling: "touch",
    overscrollBehaviorY: "contain",

    paddingBottom:
      "max(16px, env(safe-area-inset-bottom))",

    boxSizing: "border-box",

    ...theme.scrollbarStyles,
  },

  logoImg: {
    display: "block",
    margin: "0 auto",
    width: "auto",
    height: 78,
    maxWidth: 210,
    objectFit: "contain",
    padding: "8px 12px",
  },
  hideLogo: { display: "none" },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border:
      theme.mode === "light"
        ? "2px solid rgba(255, 255, 255, 0.7)"
        : "2px solid rgba(148, 163, 184, 0.5)",
  },

  compressIconButton: {
    [theme.breakpoints.down("sm")]: { padding: 6 },
  },

  topbarV2Left: {
    flexShrink: 0,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  topbarV2Menu: {
    flexShrink: 0,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: 12,
    color: "#FFFFFF",
    border: "1px solid rgba(255,255,255,.18)",
    background: "rgba(255,255,255,.10)",
    transition: "all .18s ease",

    "&:hover": {
      background: "rgba(255,255,255,.18)",
      transform: "translateY(-1px)",
    },

    "& .MuiSvgIcon-root": {
      fontSize: 23,
    },

    [theme.breakpoints.down("sm")]: {
      width: 42,
      height: 42,
      borderRadius: 13,
    },
  },

  topbarV2Identity: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 9,
  },

  topbarV2Mark: {
    flexShrink: 0,
    width: 29,
    height: 29,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 9,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#1D6BFF,#30B9FF)",
    boxShadow: "0 7px 17px rgba(29,107,255,.28)",
    fontSize: 15,
    fontWeight: 900,
    fontStyle: "italic",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  topbarV2IdentityText: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.1,
  },

  topbarV2Brand: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: ".10em",
    whiteSpace: "nowrap",

    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },

  topbarV2Page: {
    maxWidth: 180,
    marginTop: 3,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    color: "rgba(255,255,255,.58)",
    fontSize: 10,
    fontWeight: 500,

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  topbarV2Spacer: {
    flex: 1,
    minWidth: 6,
  },

  topbarV2Right: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 7,

    [theme.breakpoints.down("sm")]: {
      gap: 5,
    },
  },

  topbarV2Credits: {
    flexShrink: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",

    "& > *": {
      margin: "0 !important",
    },

    [theme.breakpoints.down("sm")]: {
      maxWidth: 142,
      overflow: "hidden",
    },
  },

  topbarV2Notification: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    "& .MuiIconButton-root": {
      width: 38,
      height: 38,
      padding: 7,
      borderRadius: 11,
      color: "#FFFFFF",
      border: "1px solid rgba(255,255,255,.15)",
      background: "rgba(255,255,255,.08)",
    },

    "& .MuiIconButton-root:hover": {
      background: "rgba(255,255,255,.16)",
    },
  },

  topbarV2User: {
    flexShrink: 0,
    minWidth: 0,
    height: 42,
    padding: "3px 8px 3px 4px",
    display: "flex",
    alignItems: "center",
    gap: 7,
    borderRadius: 13,
    color: "#FFFFFF",
    border: "1px solid rgba(255,255,255,.16)",
    background: "rgba(255,255,255,.09)",
    cursor: "pointer",
    transition: "all .18s ease",

    "&:hover": {
      background: "rgba(255,255,255,.16)",
    },

    [theme.breakpoints.down("sm")]: {
      width: 42,
      minWidth: 42,
      padding: 3,
      justifyContent: "center",
    },
  },

  topbarV2Avatar: {
    width: 32,
    height: 32,
    color: "#FFFFFF",
    backgroundColor: "#155EEF",
    border: "2px solid rgba(255,255,255,.62)",
    fontSize: 11,
    fontWeight: 800,
  },

  topbarV2UserText: {
    minWidth: 0,
    maxWidth: 95,
    display: "flex",
    flexDirection: "column",
    textAlign: "left",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  topbarV2UserName: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    color: "#FFFFFF",
    fontSize: 11.5,
    fontWeight: 700,
  },

  topbarV2UserRole: {
    marginTop: 2,
    color: "rgba(255,255,255,.54)",
    fontSize: 9,
    fontWeight: 500,
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.4)", opacity: 0 },
  },
}))(Badge);

const getTodayInSaoPaulo = () => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(new Date());
};

const isCompanySubscriptionExpired = user => {
  const company = user?.company;

  if (!company) {
    return false;
  }

  const isSuperUser =
    Boolean(user?.super) ||
    user?.profile === "super";

  if (isSuperUser) {
    return false;
  }

  if (company.status === false) {
    return true;
  }

  const dueDate = company.dueDate
    ? String(company.dueDate).slice(0, 10)
    : null;

  if (!dueDate) {
    return false;
  }

  return dueDate < getTodayInSaoPaulo();
};

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userToken, setUserToken] = useState("disabled");
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    handleLogout,
    loading,
    user,
    socket,
    refreshCurrentUser
  } = useContext(AuthContext);

  const {
    whatsApps = []
  } = useContext(WhatsAppsContext);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const subscriptionBlocked =
    isCompanySubscriptionExpired(user);

  const currentSubscriptionPath =
    window.location.pathname;

  const isSubscriptionPage =
    currentSubscriptionPath === "/financeiro" ||
    currentSubscriptionPath.startsWith("/financeiro/");

  const showSubscriptionOverlay =
    subscriptionBlocked && !isSubscriptionPage;

  const companyDueDate =
    user?.company?.dueDate
      ? new Date(user.company.dueDate).toLocaleDateString(
          "pt-BR",
          {
            timeZone: "America/Sao_Paulo"
          }
        )
      : null;

  const hasPreviousSubscription =
    Boolean(user?.company?.recurrence) ||
    Boolean(user?.company?.dueDate);

  const blockedTitle = hasPreviousSubscription
    ? "Sua assinatura expirou"
    : "Seu período de teste terminou";

  const blockedMessage = hasPreviousSubscription
    ? `O acesso da sua empresa venceu${
        companyDueDate
          ? ` em ${companyDueDate}`
          : ""
      }. Regularize sua assinatura para continuar usando a Korax.`
    : "Seu período gratuito chegou ao fim. Escolha um plano para continuar usando a Korax.";

  // Redirecionamento para onboarding removido

  const [showOnboardingBanner, setShowOnboardingBanner] = useState(() => {
    try {
      const done = JSON.parse(localStorage.getItem("onboarding_done") || "{}");
      const dismissed = localStorage.getItem("onboarding_global_banner") === "true";
      const totalDone = Object.values(done).filter(Boolean).length;
      return !dismissed && totalDone < 6;
    } catch { return true; }
  });

  const dismissOnboardingBanner = () => {
    setShowOnboardingBanner(false);
    localStorage.setItem("onboarding_global_banner", "true");
  };
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const history = useHistory();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState({ contacts: [], tickets: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const searchRef = React.useRef(null);
  const searchTimeout = React.useRef(null);

  const allResults = [
    ...searchResults.contacts.map(ct => ({ type: "contact", label: ct.name, sub: ct.number, id: ct.id, icon: "👤" })),
    ...searchResults.tickets.map(t => ({ type: "ticket", label: t.contact?.name || "Ticket #"+t.id, sub: "#"+t.id+" • "+t.queue?.name, id: t.id, uuid: t.uuid, icon: "💬" })),
  ];

  const fetchSuggestions = async (val) => {
    if (!val || val.length < 2) { setSearchResults({ contacts: [], tickets: [] }); return; }
    setSearchLoading(true);
    try {
      const [rc, rt] = await Promise.all([
        api.get("/contacts", { params: { searchParam: val, pageNumber: 1 } }),
        api.get("/tickets", { params: { searchParam: val, pageNumber: 1, status: "open" } }),
      ]);
      setSearchResults({ contacts: (rc.data?.contacts || []).slice(0, 4), tickets: (rt.data?.tickets || []).slice(0, 3) });
    } catch {}
    setSearchLoading(false);
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    setSelectedIdx(-1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (item) => {
    if (item.type === "contact") history.push("/contacts?search=" + encodeURIComponent(item.label));
    if (item.type === "ticket") history.push("/tickets/" + (item.uuid || item.id));
    setSearchValue(""); setSearchFocused(false); setSearchResults({ contacts: [], tickets: [] });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, allResults.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, -1)); }
    if (e.key === "Enter") {
      if (selectedIdx >= 0 && allResults[selectedIdx]) handleSelect(allResults[selectedIdx]);
      else if (searchValue.trim()) { history.push("/contacts?search=" + encodeURIComponent(searchValue.trim())); setSearchValue(""); setSearchFocused(false); }
    }
    if (e.key === "Escape") { setSearchValue(""); setSearchFocused(false); setSearchResults({ contacts: [], tickets: [] }); }
  };

  const PAGE_LABELS = {
    "/": { label: "Dashboard", icon: "📊" },
    "/tickets": { label: "Atendimentos", icon: "💬" },
    "/contacts": { label: "Contatos", icon: "👥" },
    "/kanban": { label: "Kanban", icon: "📋" },
    "/schedules": { label: "Follow-up", icon: "📅" },
    "/users": { label: "Equipe", icon: "👤" },
    "/prompts": { label: "CavIA", icon: "🤖" },
    "/disparos": { label: "Campanhas", icon: "🚀" },
    "/connections": { label: "Conexoes", icon: "📱" },
    "/chats": { label: "Chat Interno", icon: "💭" },
    "/helps": { label: "Ajuda", icon: "🎓" },
    "/onboarding": { label: "Onboarding", icon: "🚀" },
  };

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const [notificationSound, setNotificationSound] = useState(
    localStorage.getItem("notificationSound") || "classic"
  );
  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);

  const settings = useSettings();

  useEffect(() => {
    const getSetting = async () => {
      try {
        await settings.get("wtV");
        setUserToken("disabled");
      } catch (error) {
        // Ao deslogar, a API pode responder 401 antes do redirect para /login.
        // Não tratamos isso como erro fatal de tela.
        if (error?.response?.status !== 401) {
          console.error("Erro ao carregar setting wtV", error);
        }
      }
    };
    getSetting();
  }, [settings]);

  useEffect(() => {
    const syncDrawerWithViewport = () => {
      const isDesktop = window.innerWidth >= 960;

      if (!isDesktop) {
        setDrawerOpen(false);
        return;
      }

      setDrawerOpen(user.defaultMenu !== "closed");
    };

    syncDrawerWithViewport();

    window.addEventListener("resize", syncDrawerWithViewport);

    return () => {
      window.removeEventListener("resize", syncDrawerWithViewport);
    };
  }, [user.defaultMenu]);

  useEffect(() => {
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultTheme, theme.mode]);

  useEffect(() => {
    if (document.body.offsetWidth < 960) setDrawerVariant("temporary");
    else setDrawerVariant("permanent");
  }, [drawerOpen]);

  useEffect(() => {
    const handleGuideDrawer = event => {
      if (window.innerWidth >= 960) {
        setDrawerOpen(Boolean(event.detail?.open));
      }
    };

    window.addEventListener("koraxGuideDrawer", handleGuideDrawer);

    return () => {
      window.removeEventListener("koraxGuideDrawer", handleGuideDrawer);
    };
  }, []);

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    if (companyId) {
      const ImageUrl = user.profileImage;
      if (ImageUrl !== undefined && ImageUrl !== null)
        setProfileUrl(`${backendUrl}/public/avatar/${ImageUrl}`);
      else setProfileUrl(`${process.env.FRONTEND_URL}/nopicture.png`);

      const onCompanyAuthLayout = (data) => {
        if (data.user.id === +userId) {
          toastError("Sua conta foi acessada em outro computador.");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
      };
    }
  }, [socket]);


  useEffect(() => {
    const companyId = user?.companyId;

    if (
      !companyId ||
      !socket ||
      typeof socket.on !== "function" ||
      typeof refreshCurrentUser !== "function"
    ) {
      return undefined;
    }

    const onSubscriptionUpdated = async payload => {
      if (
        payload?.companyId &&
        Number(payload.companyId) !== Number(companyId)
      ) {
        return;
      }

      try {
        const refreshedUser =
          await refreshCurrentUser();

        if (
          refreshedUser?.company?.status !== false
        ) {
          toast.success(
            "Pagamento confirmado. Seu acesso à Korax foi liberado."
          );
        }
      } catch (error) {
        console.error(
          "[Subscription Realtime] Erro ao sincronizar assinatura:",
          error
        );
      }
    };

    socket.on(
      "subscription.updated",
      onSubscriptionUpdated
    );

    return () => {
      socket.off(
        "subscription.updated",
        onSubscriptionUpdated
      );
    };
  }, [
    socket,
    user?.companyId,
    refreshCurrentUser
  ]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };
  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };
  const handleClickLogout = async () => {
    handleCloseMenu();

    try {
      await handleLogout();
    } finally {
      window.location.replace("/login");
    }
  };
  const handleRefreshPage = () => window.location.reload(false);

  const primaryWhatsapp =
    whatsApps.find(item =>
      item.channel === "whatsapp" &&
      item.isDefault
    ) ||
    whatsApps.find(item =>
      item.channel === "whatsapp"
    );

  const whatsappNeedsAttention =
    Boolean(primaryWhatsapp) &&
    primaryWhatsapp.status !== "CONNECTED" &&
    primaryWhatsapp.status !== "OPENING";

  const whatsappAlertText =
    primaryWhatsapp?.status === "qrcode"
      ? "Seu WhatsApp está aguardando a leitura do QR Code."
      : "Seu WhatsApp foi desconectado. Sua IA está temporariamente pausada.";

  if (loading) return <BackdropLoading />;

  // src da logo com fallback ao tema
  const logoSrc =
    theme.mode === "light"
      ? (typeof theme.calculatedLogoLight === "function"
          ? theme.calculatedLogoLight()
          : logo)
      : (typeof theme.calculatedLogoDark === "function"
          ? theme.calculatedLogoDark()
          : logoDark);

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClose),
        }}
        open={drawerOpen}
        onClose={() => { if (drawerVariant === "temporary") setDrawerOpen(false); }}
      >
        <div className={classes.toolbarIcon}>
          {/* Logo visível no Drawer */}
          <img
            src={logoSrc}
            alt="logo"
            className={drawerOpen ? classes.logoImg : classes.hideLogo}
          />
          <IconButton
            onClick={() => setDrawerOpen(!drawerOpen)}
            style={{
              width: 32, height: 32,
              background: "linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)",
          border: "1px solid rgba(59,130,246,0.28)",
          borderRadius: 10,
          color: "#ffffff",
          marginLeft: drawerOpen ? "auto" : 0,
          boxShadow: "0 8px 22px rgba(15,23,42,0.18)",
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}
          >
            {drawerOpen
              ? <ChevronLeftIcon style={{ fontSize: 18 }} />
              : <ChevronRightIcon style={{ fontSize: 18 }} />
            }
          </IconButton>
        </div>
        <List className={classes.containerWithScroll}>
          <MainListItems collapsed={!drawerOpen} />
        </List>
        <Divider />
      </Drawer>

      <AppBar
        position="fixed"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar
          variant="dense"
          className={classes.toolbar}
          style={{
            minHeight: 56,
            height: 56,
            paddingLeft: 10,
            paddingRight: 10
          }}
        >
          <div className={classes.topbarV2Left}>
            <IconButton
              onClick={() => setDrawerOpen(prev => !prev)}
              className={classes.topbarV2Menu}
              aria-label="Abrir menu principal"
              title="Abrir menu"
            >
              <MenuIcon />
            </IconButton>

            <div className={classes.topbarV2Identity}>
              <div className={classes.topbarV2Mark}>
                K
              </div>

              <div className={classes.topbarV2IdentityText}>
                <span className={classes.topbarV2Brand}>
                  KORAX
                </span>

                <span className={classes.topbarV2Page}>
                  {PAGE_LABELS[location.pathname]?.label ||
                    (location.pathname.startsWith("/tickets")
                      ? "Conversas"
                      : "Dashboard")}
                </span>
              </div>
            </div>
          </div>

          <div className={classes.topbarV2Spacer} />

          <div className={classes.topbarV2Right}>
            <div className={classes.topbarV2Credits}>
              <AICreditsWidget />
            </div>

            <div className={classes.topbarV2Notification}>
              <NotificationsVolume
                volume={volume}
                setVolume={setVolume}
                notificationSound={notificationSound}
                setNotificationSound={setNotificationSound}
              />
            </div>

            <div className={classes.topbarV2Notification}>
              <NotificationsPopOver
                volume={volume}
                notificationSound={notificationSound}
              />
            </div>

            <button
              type="button"
              className={classes.topbarV2User}
              onClick={handleMenu}
              aria-label="Abrir opções do usuário"
            >
              <Avatar
                className={classes.topbarV2Avatar}
                src={profileUrl}
              >
                {(user?.name || "K")
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map(part => part.charAt(0))
                  .join("")
                  .toUpperCase()}
              </Avatar>

              <span className={classes.topbarV2UserText}>
                <span className={classes.topbarV2UserName}>
                  {user?.name?.split(" ")[0] || "Usuário"}
                </span>

                <span className={classes.topbarV2UserRole}>
                  {user?.profile === "admin"
                    ? "Administrador"
                    : "Equipe"}
                </span>
              </span>

              <span
                style={{
                  color: "rgba(255,255,255,.62)",
                  fontSize: 11
                }}
              >
                ▾
              </span>
            </button>
          </div>

          <UserModal
            open={userModalOpen}
            onClose={() => setUserModalOpen(false)}
            onImageUpdate={(url) => setProfileUrl(url)}
            userId={user?.id}
          />

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            keepMounted
            open={Boolean(menuOpen && anchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{
              style: {
                minWidth: 210,
                marginTop: 8,
                padding: 4,
                overflow: "hidden",
                borderRadius: 14,
                border:
                  theme.mode === "light"
                    ? "1px solid #E2E8F0"
                    : "1px solid rgba(148,163,184,.18)",
                background:
                  theme.mode === "light"
                    ? "#FFFFFF"
                    : "#0D1626",
                boxShadow: "0 14px 38px rgba(15,23,42,.18)"
              }
            }}
          >
            <div
              style={{
                padding: "12px 12px 10px",
                marginBottom: 4,
                borderBottom:
                  theme.mode === "light"
                    ? "1px solid #F1F5F9"
                    : "1px solid rgba(148,163,184,.14)"
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10
                }}
              >
                <Avatar
                  src={profileUrl}
                  style={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    color: "#FFFFFF",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg,#0066FF,#00A3FF)"
                  }}
                >
                  {user?.name?.slice(0, 2).toUpperCase()}
                </Avatar>

                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      color:
                        theme.mode === "light"
                          ? "#0F172A"
                          : "#F1F5F9",
                      fontSize: "0.84rem",
                      fontWeight: 700
                    }}
                  >
                    {user?.name}
                  </div>

                  <div
                    style={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      color: "#94A3B8",
                      fontSize: "0.7rem"
                    }}
                  >
                    {user?.email}
                  </div>
                </div>
              </div>
            </div>

            <MenuItem
              onClick={handleOpenUserModal}
              style={{
                minHeight: 40,
                margin: "2px 4px",
                padding: "8px 10px",
                gap: 8,
                borderRadius: 8,
                color:
                  theme.mode === "light"
                    ? "#0F172A"
                    : "#F1F5F9",
                fontSize: "0.84rem",
                fontWeight: 600
              }}
            >
              👤 {i18n.t("mainDrawer.appBar.user.profile")}
            </MenuItem>

            <MenuItem
              onClick={handleClickLogout}
              style={{
                minHeight: 40,
                margin: "2px 4px",
                padding: "8px 10px",
                gap: 8,
                borderRadius: 8,
                color: "#EF4444",
                fontSize: "0.84rem",
                fontWeight: 600
              }}
            >
              🚪 {i18n.t("mainDrawer.appBar.user.logout")}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <style>{`
        #header-search::placeholder { color: rgba(200, 220, 255, 0.7) !important; }
        #header-search { color: #ffffff !important; }
        .topbar-actions-scroll::-webkit-scrollbar { display: none; }
      `}</style>
      <KoraxAssistant />
      <KoraxGuide />

      {showSubscriptionOverlay && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            overflowY: "auto",
            background: "rgba(2, 6, 23, 0.78)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)"
          }}
          onMouseDown={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 580,
              padding: "34px 32px 28px",
              borderRadius: 26,
              color: "#0F172A",
              background: "#FFFFFF",
              border: "1px solid rgba(255,255,255,.72)",
              boxShadow: "0 35px 100px rgba(2,6,23,.45)",
              textAlign: "center"
            }}
            onClick={event => event.stopPropagation()}
          >
            <div
              style={{
                width: 64,
                height: 64,
                margin: "0 auto 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 20,
                color: "#FFFFFF",
                background:
                  "linear-gradient(135deg,#1D4ED8,#2563EB)",
                boxShadow:
                  "0 16px 34px rgba(37,99,235,.28)",
                fontSize: 28,
                fontWeight: 900
              }}
            >
              K
            </div>

            <div
              style={{
                color: "#1D4ED8",
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: ".13em",
                textTransform: "uppercase"
              }}
            >
              Acesso pausado
            </div>

            <h1
              style={{
                margin: "10px 0 0",
                color: "#0F172A",
                fontSize: "clamp(24px, 4vw, 34px)",
                lineHeight: 1.12,
                letterSpacing: "-.035em"
              }}
            >
              {blockedTitle}
            </h1>

            <p
              style={{
                maxWidth: 465,
                margin: "14px auto 0",
                color: "#64748B",
                fontSize: 14,
                lineHeight: 1.65
              }}
            >
              {blockedMessage}
            </p>

            <div
              style={{
                marginTop: 22,
                padding: "14px 16px",
                borderRadius: 14,
                color: "#334155",
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                fontSize: 12.5,
                lineHeight: 1.55,
                textAlign: "left"
              }}
            >
              <div
                style={{
                  color: "#0F172A",
                  fontWeight: 850,
                  marginBottom: 5
                }}
              >
                Sua operação permanece protegida
              </div>

              Conversas, configurações, IA, CRM e histórico continuam
              salvos. O acesso será restaurado após a confirmação do
              pagamento.
            </div>

            <button
              type="button"
              onClick={() => {
                if (
                  window.location.pathname !== "/financeiro"
                ) {
                  history.replace("/financeiro");
                }
              }}
              style={{
                width: "100%",
                minHeight: 50,
                marginTop: 22,
                border: "none",
                borderRadius: 13,
                color: "#FFFFFF",
                background:
                  "linear-gradient(135deg,#155EEF,#1D4ED8)",
                boxShadow:
                  "0 14px 28px rgba(37,99,235,.24)",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: 900
              }}
            >
              Escolher plano e continuar
            </button>

            <button
              type="button"
              onClick={handleClickLogout}
              style={{
                marginTop: 12,
                padding: "9px 16px",
                border: "none",
                color: "#64748B",
                background: "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12.5,
                fontWeight: 750
              }}
            >
              Sair da conta
            </button>

            <div
              style={{
                marginTop: 8,
                color: "#94A3B8",
                fontSize: 10.5,
                lineHeight: 1.5
              }}
            >
              O acesso será liberado automaticamente após a confirmação
              do pagamento.
            </div>
          </div>
        </div>
      )}

      <main
        className={classes.content}
        aria-hidden={showSubscriptionOverlay ? "true" : undefined}
        style={{
          pointerEvents: showSubscriptionOverlay
            ? "none"
            : "auto",
          userSelect: showSubscriptionOverlay
            ? "none"
            : "auto",
          filter: showSubscriptionOverlay
            ? "blur(3px)"
            : "none"
        }}
      >
        <div className={classes.appBarSpacer} />
        {false && showOnboardingBanner && (
          <div style={{
            flexShrink: 0, margin: "10px 16px 0",
            background: "linear-gradient(135deg, #0055FF 0%, #6C5CE7 100%)",
            borderRadius: 14, padding: "12px 16px",
            display: "flex", alignItems: "center", gap: 12,
            boxShadow: "0 4px 16px rgba(0,85,255,0.2)",
            animation: "slideDown 0.4s ease",
          }}>
            <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>👊</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#fff", lineHeight: 1.2 }}>
                Bem-vindo ao CAVChat!
              </div>
              <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                Siga o checklist de ativação e configure sua operação em minutos
              </div>
            </div>
            <button
              onClick={() => { history.push("/onboarding"); dismissOnboardingBanner(); }}
              style={{
                background: "#fff", color: "#0055FF", border: "none",
                borderRadius: 9, padding: "7px 16px", fontWeight: 700,
                fontSize: "0.78rem", cursor: "pointer", flexShrink: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              Ver checklist →
            </button>
            <button onClick={dismissOnboardingBanner} style={{
              background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.8)", borderRadius: 7, width: 28, height: 28,
              cursor: "pointer", fontSize: "1rem", display: "flex", alignItems: "center",
              justifyContent: "center", flexShrink: 0,
            }}>×</button>
          </div>
        )}
        {whatsappNeedsAttention && Number(user?.companyId) !== 55 && (
          <div
            role="alert"
            style={{
              flexShrink: 0,
              margin: "10px 12px 8px",
              padding: "7px 9px",
              minHeight: 42,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "nowrap",
              gap: 8,
              borderRadius: 11,
              color: "#991B1B",
              background:
                "linear-gradient(135deg, rgba(254,242,242,.98), rgba(255,255,255,.98))",
              border: "1px solid rgba(239,68,68,.18)",
              boxShadow: "0 3px 12px rgba(220,38,38,.06)"
            }}
          >
            <div
              style={{
                minWidth: 0,
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
                overflow: "hidden"
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 9,
                  color: "#DC2626",
                  background: "rgba(239,68,68,.09)",
                  border: "1px solid rgba(239,68,68,.10)",
                  fontSize: 13,
                  fontWeight: 900
                }}
              >
                !
              </span>

              <div
                style={{
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  overflow: "hidden",
                  whiteSpace: "nowrap"
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    color: "#991B1B",
                    fontSize: 11.5,
                    fontWeight: 800,
                    lineHeight: 1.2
                  }}
                >
                  WhatsApp desconectado
                </span>

                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    color: "rgba(185,28,28,.35)",
                    fontSize: 10
                  }}
                >
                  •
                </span>

                <span
                  title={whatsappAlertText}
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    color: "#B91C1C",
                    fontSize: 10.5,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {whatsappAlertText}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => history.push("/connections")}
              title="Reconectar WhatsApp"
              aria-label="Reconectar WhatsApp"
              style={{
                height: 30,
                minWidth: 30,
                padding: "0 9px",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
                border: "1px solid rgba(220,38,38,.18)",
                borderRadius: 8,
                color: "#B91C1C",
                background: "rgba(255,255,255,.88)",
                fontSize: 10.5,
                fontWeight: 800,
                lineHeight: 1,
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(220,38,38,.06)"
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  fontSize: 15,
                  lineHeight: 1
                }}
              >
                ↻
              </span>

              <span
                className="whatsapp-alert-button-label"
              >
                Reconectar
              </span>
            </button>

            <style>
              {`
                @media (max-width: 600px) {
                  .whatsapp-alert-button-label {
                    display: none;
                  }
                }
              `}
            </style>
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "row" }}>
          <div className={classes.pageViewport}>
            {children || null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoggedInLayout;
